import CardLoading from '@op/components/CardLoading';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { FilterGameInfoContext } from '@op/components/FilterCommon/context';
import useLanguage from '@op/hooks/useLanguage';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { LanguageType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Segment } from 'tea-component';
import MarketContentBody from '@opm/components/MarketContentBody';
import { FilterCondGameIdsProvider } from '@opm/components/FilterCommon/context/FilterCondGameIdsContext';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { getMultiGamesCond } from '@opm/services';
import { TGameMap } from '@opm/pages/ZoneTrendingPublications/hooks/useMultiGamesCond';
import ZoneTrendingVideo from '@opm/pages/ZoneTrendingPublications/components/ZoneTrendingVideo';
import ZoneTrendingPosts from '@opm/pages/ZoneTrendingPublications/components/ZoneTrendingPosts';
import { STUDIO_TRENDING_PUBLICATIONS_FILTER_INSTANCE_ID } from '../TrendingPostsVdieoWrapper/types';
import '@opm/pages/ZoneTrendingPublications/index.scss';

const { Content } = Layout;

type ValueType = 'official' | 'player' | 'total';
type ContentType = 'video' | 'posts';

interface IZoneTrendingPublicationsAdapterProps {
  contentType?: ContentType;
  sourcePathname?: string;
  condGameIds?: string[];
}

const DEFAULT_SOURCE_PATHNAME = '/marketing/MahjongSolitaire/TrendingPublications';

const ZoneTrendingPublicationsAdapterInner: React.FC<Omit<IZoneTrendingPublicationsAdapterProps, 'condGameIds'>> = ({
  contentType = 'video',
  sourcePathname = DEFAULT_SOURCE_PATHNAME,
}) => {
  const { formatOptionsMessage } = useLanguage();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const [value, setValue] = useState<ValueType>('total');
  const [multiGamesCondLoading, setMultiGamesCondLoading] = useState(false);
  const [multiGamesCondData, setMultiGamesCondData] = useState<any>({});

  const {
    loading: marketingCondLoading,
    condData: marketingCondData,
  } = useFilterCondData({
    keys: ['ADGame'],
    extrRequestKeys: [],
  });

  const zoneGames = useMemo(
    () => marketingCondData?.materials_config_games || [],
    [marketingCondData],
  );

  const scopedGames = useMemo(
    () =>
      zoneGames.map(game => ({
        edition_unified_id: game.game_id,
        entity_type: game.entity_type || 'mobile',
        id_type: 'unified' as const,
      })),
    [zoneGames],
  );

  const scopedGameMap: TGameMap = useMemo(() => {
    const map = new Map<string, { name: string; cover: string; }>();
    zoneGames.forEach(game => {
      map.set(game.game_id, { name: game.game_name, cover: game.cover || '' });
    });
    return map;
  }, [zoneGames]);

  useEffect(() => {
    if (marketingCondLoading) {
      return;
    }

    if (zoneGames.length === 0) {
      setMultiGamesCondData({ zone_games: [] });
      setMultiGamesCondLoading(false);
      return;
    }

    let cancelled = false;
    setMultiGamesCondLoading(true);

    getMultiGamesCond({
      games: scopedGames,
      request_type: [
        'region_language',
        'channel',
        'topic',
        'key_topic',
        'keyword_mutual_account_config',
        'region_country',
        'region_country',
      ],
      un_request_type: ['topic_trans'],
    })
      .then((result: any) => {
        if (!cancelled) {
          setMultiGamesCondData({
            ...(result || {}),
            zone_games: zoneGames,
          });
        }
      })
      .catch((err: any) => {
        console.error('multi_games_cond error:', err);
        if (!cancelled) {
          setMultiGamesCondData({ zone_games: zoneGames });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMultiGamesCondLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [marketingCondLoading, scopedGames, zoneGames]);

  const condLoading = marketingCondLoading || multiGamesCondLoading;
  const condData = multiGamesCondData;

  const fakeGameInfo = useMemo(
    () => ({
      edition_unified_id: sourcePathname,
      entity_type: 'mobile',
      id_type: 'unified',
      filterInstanceId: STUDIO_TRENDING_PUBLICATIONS_FILTER_INSTANCE_ID,
    }),
    [sourcePathname],
  );

  const enrichedCondData = useMemo(() => {
    if (condLoading) {
      return undefined;
    }
    if (zoneGames.length === 0) {
      return { zone_games: [] };
    }
    return condData;
  }, [condLoading, zoneGames.length, condData]);

  const {
    scrollBody,
    loading: filterLoading,
    filter,
    changeFilter,
    filterOptions,
    userSynchronousValue,
    confirmRef,
  } = useFilterDataHook({
    gameInfo: fakeGameInfo,
    pageName: 'Marketing_Trending_Publications',
    defaultUserSynchronousValue: {
      social_media_by_account: ['99999'],
      social_media_by_keyword: ['99999'],
      external_data_sources: ['99999'],
    },
  });

  useEffect(() => {
    if (condLoading || filter || scopedGames.length === 0) {
      return;
    }
    changeFilter({
      ...DBO_TM.utcFilterRangeDay(6, 0),
      zone_games: [],
    });
  }, [changeFilter, condLoading, filter, scopedGames.length]);

  const filteredGameMap = useMemo(() => {
    const selectedIds: string[] = filter?.zoneGames || [];
    if (selectedIds.length === 0) return scopedGameMap;
    const selectedSet = new Set(selectedIds);
    const filtered: TGameMap = new Map();
    scopedGameMap.forEach((val, key) => {
      if (selectedSet.has(key)) filtered.set(key, val);
    });
    return filtered;
  }, [filter?.zoneGames, scopedGameMap]);

  const realFilter = useMemo(() => {
    if (!filter || scopedGames.length === 0) {
      return undefined;
    }
    const { zone_games: selectedIds, ...restFilter } = filter || {};
    const finalGames = selectedIds?.length > 0
      ? scopedGames.filter(g => selectedIds.includes(g.edition_unified_id))
      : scopedGames;
    return {
      ...restFilter,
      games: finalGames,
      organization: value === 'total' ? '' : value,
    };
  }, [filter, scopedGames, value]);

  const contentLoading = filterLoading || (scopedGames.length > 0 && !realFilter);

  return (
    <FilterGameInfoContext.Provider
      value={{
        gameInfo: fakeGameInfo,
        use: true,
        wholeGameInfo: {},
      }}
    >
      <Content className="dbom-zone-trending-page" ref={scrollBody}>
        <MarketContentBody loading={condLoading}>
          {enrichedCondData && (
            <FilterFixed
              scrollBody={scrollBody}
              filter={filterOptions}
              confirmRef={confirmRef}
              userSynchronousValue={userSynchronousValue}
              defaultValue={DBO_TM.utcFilterRangeDay(6, 0)}
              defaultConfig={{
                channel: { channelKey: 'social' },
                keyWords: { hideSystemTab: true }
              }}
              isNoRequest
              externalCondData={enrichedCondData}
              confirm={(value: any) => {
                changeFilter(value);
              }}
              operateShowOptions={['more']}
            />
          )}

          <div className="dbom-zone-trending-content-loading">
            <CardLoading loading={contentLoading}>
              <div className="dbo-filter-translateSelect">
                <Segment
                  className="segment-light"
                  value={value.toString()}
                  onChange={(next: ValueType) => {
                    setValue(next);
                  }}
                  options={formatOptionsMessage([
                    { text: 'Total', value: 'total' },
                    { text: 'Official Account', value: 'official' },
                    { text: 'Earned Content', value: 'player' },
                  ])}
                />
                <TranslateSelect
                  langOptions={formatOptionsMessage(languageSelectOptions)}
                  lang={language}
                  isSingle
                  getChange={(e: any) => setLanguage(e)}
                />
              </div>
              {realFilter && (
                contentType === 'posts' ? (
                  <ZoneTrendingPosts
                    key="posts"
                    filter={realFilter}
                    gameMap={filteredGameMap}
                    language={language}
                    loadingHeight={600}
                    emptyHeight={600}
                  />
                ) : (
                  <ZoneTrendingVideo
                    key="video"
                    filter={realFilter}
                    gameMap={filteredGameMap}
                    language={language}
                    loadingHeight={600}
                    emptyHeight={600}
                  />
                )
              )}
            </CardLoading>
          </div>
        </MarketContentBody>
      </Content>
    </FilterGameInfoContext.Provider>
  );
};

const ZoneTrendingPublicationsAdapter: React.FC<IZoneTrendingPublicationsAdapterProps> = ({
  condGameIds,
  ...props
}) => (
  <FilterCondGameIdsProvider gameIds={condGameIds}>
    <ZoneTrendingPublicationsAdapterInner {...props} />
  </FilterCondGameIdsProvider>
);

export default ZoneTrendingPublicationsAdapter;
